/**
 * MASAV bulk receipt: group draft/selected rows by client and sum monetary amounts.
 * "Saved amount includes VAT" maps from PayableAmount / SavedAmountIncludesVAT-style fields on each row.
 */

export function getRowSavedAmountIncludesVat(row) {
    if (!row || typeof row !== 'object') return 0;
    const v =
        row.SavedAmountIncludesVAT ??
        row.savedAmountIncludesVat ??
        row.PayableAmount ??
        row.payableAmount ??
        0;
    return Number(v) || 0;
}

/** Charge descriptions behind a row's payable (see MasavDraftRowDTO.ChargeDescriptions). */
export function getRowChargeDescriptions(row) {
    const v = row?.chargeDescriptions ?? row?.ChargeDescriptions ?? [];
    return Array.isArray(v) ? v.map((d) => String(d ?? '').trim()).filter(Boolean) : [];
}

/**
 * @returns {Array<{ caseId: string, clientName: string, rows: object[], totalSavedAmountIncludesVAT: number }>}
 */
export function groupMasavBulkRowsByClient(selectedRows = []) {
    const map = new Map();
    for (const row of selectedRows) {
        const clientKey = row.caseId ?? row.CaseId ?? row.clientId ?? row.ClientId;
        if (clientKey == null || clientKey === '') continue;
        const key = String(clientKey);
        const amt = getRowSavedAmountIncludesVat(row);
        if (!map.has(key)) {
            map.set(key, {
                caseId: key,
                clientName: row.clientName ?? row.ClientName ?? '',
                rows: [],
                totalSavedAmountIncludesVAT: 0,
                chargeDescriptions: [],
            });
        }
        const g = map.get(key);
        g.rows.push(row);
        g.totalSavedAmountIncludesVAT += amt;
        for (const desc of getRowChargeDescriptions(row)) {
            if (!g.chargeDescriptions.includes(desc)) g.chargeDescriptions.push(desc);
        }
    }
    return Array.from(map.values());
}

/**
 * One entry per client for MasavBulkReceiptModal state (single- and multi-row clients use the same shape).
 */
export function buildPerClientStateFromSelection(selectedRows = []) {
    const groups = groupMasavBulkRowsByClient(selectedRows);
    return groups.map((g) => {
        const total = Number(g.totalSavedAmountIncludesVAT) || 0;
        return {
            caseId: g.caseId,
            clientName: g.clientName,
            sourceRows: g.rows,
            rowCount: g.rows.length,
            totalSavedAmountIncludesVAT: total,
            amount: total > 0 ? String(total) : '',
            include: total > 0,
            /** Client's own charge description(s), usable instead of the shared payment description. */
            itemDescription: g.chargeDescriptions.join(', '),
        };
    });
}
